import { useRef, useState } from 'react'
import { LayoutDashboard } from 'lucide-react'
import { useTaskStore } from '../../store/taskStore'
import CanvasWidgetWrapper from './CanvasWidget'
import FloatingMenu from './FloatingMenu'

export default function CanvasPage() {
  const calmVisuals = useTaskStore((s) => s.accessibility.calmVisuals)
  const canvasWidgets = useTaskStore((s) => {
    const currentPage = s.pages.find((p) => p.id === s.currentPageId)
    return currentPage?.canvasWidgets ?? []
  })
  const arrangeCanvasWidgets = useTaskStore((s) => s.arrangeCanvasWidgets)
  const canvasRef = useRef<HTMLDivElement>(null)
  const [organizedHeight, setOrganizedHeight] = useState<number | null>(null)

  const organizeAndFill = () => {
    const active = canvasWidgets.filter((widget) => !widget.minimized)
    if (!active.length) return
    const width = Math.max(320, canvasRef.current?.clientWidth ?? window.innerWidth)
    const availableHeight = Math.max(520, canvasRef.current?.clientHeight ?? window.innerHeight)
    const padding = 16
    const gap = 16
    const toolbarHeight = 56
    const columns = width < 640 ? 1 : width < 1100 ? 2 : 3
    const rows = Math.ceil(active.length / columns)
    const cardWidth = Math.floor((width - padding * 2 - gap * (columns - 1)) / columns)
    const preferredHeight = Math.max(220, Math.floor((availableHeight - toolbarHeight - padding * 2 - gap * (rows - 1)) / rows))
    const contentHeight = toolbarHeight + padding * 2 + rows * preferredHeight + Math.max(0, rows - 1) * gap
    arrangeCanvasWidgets(active.map((widget, index) => ({
      instanceId: widget.instanceId,
      x: padding + (index % columns) * (cardWidth + gap),
      y: toolbarHeight + padding + Math.floor(index / columns) * (preferredHeight + gap),
      width: cardWidth,
      height: preferredHeight,
    })))
    setOrganizedHeight(contentHeight)
  }

  return (
    <div
      ref={canvasRef}
      className="relative w-full bg-slate-100 dark:bg-slate-950 transition-colors duration-200"
      style={{ minHeight: organizedHeight ? `${organizedHeight}px` : '100%' }}
    >
      {/* Grid background pattern */}
      {!calmVisuals && (
        <div
          className="absolute inset-0 pointer-events-none opacity-40 dark:opacity-20 transition-opacity"
          style={{
            backgroundImage:
              'radial-gradient(circle, currentColor 1px, transparent 1px)',
            color: 'var(--dot-color, #94a3b8)',
            backgroundSize: '28px 28px',
          }}
        />
      )}

      {/* Empty state */}
      {canvasWidgets.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 pointer-events-none">
          <div className="text-center">
            <div className="text-6xl mb-4">🧩</div>
            <p className="text-xl font-semibold text-slate-600 dark:text-slate-300">Canvas vazio</p>
            <p className="text-sm mt-2 text-slate-500 dark:text-slate-400">
              Clique no botão <span className="font-semibold text-blue-500">⊞</span> no canto inferior direito
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">para adicionar widgets ao seu dashboard</p>
          </div>
        </div>
      )}

      {/* Widgets */}
      <div className="relative min-h-full px-3 pt-14 sm:px-0 sm:pt-0" style={{ minWidth: '100%', minHeight: '100%' }}>
        {canvasWidgets.map((widget) => (
          <CanvasWidgetWrapper key={widget.instanceId} widget={widget} />
        ))}
      </div>

      {canvasWidgets.length > 0 && (
        <button
          type="button"
          onClick={organizeAndFill}
          className="absolute right-4 top-3 z-[9997] flex items-center gap-2 rounded-xl bg-slate-900/90 dark:bg-slate-800/90 text-white backdrop-blur-md px-3.5 py-2 text-xs font-semibold shadow-lg border border-slate-700/50 hover:bg-slate-800 dark:hover:bg-slate-700 transition-all active:scale-95"
        >
          <LayoutDashboard size={15} /> Organizar e preencher
        </button>
      )}

      {/* Floating menu button */}
      <FloatingMenu />
    </div>
  )
}
